import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { Outlet } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { faPencil, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import Modal from '../../../core/components/Modal.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import Button from '../../../core/components/Button.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import { CREATE_SSO_CONFIG, DELETE_SSO_CONFIG, GET_SSO_CONFIGS } from '../../queries.ts';
import { CUSTOMER_SSO_ADD_PATH, CUSTOMER_SSO_EDIT_PATH, CUSTOMER_SSO_PATH } from '../../constants.ts';
import SsoConfigFields, { SSO_CONFIG_FORM_DEFAULT_VALUES } from './SsoConfigFields.tsx';

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);

interface SsoConfigOutletContext {
    refetch: () => Promise<any>;
}

export const AddSsoConfigFormModal = () => {
    const { t } = useTranslation('customer');
    const { refetch } = useOutletContext<SsoConfigOutletContext>();
    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const form = useForm({ defaultValues: SSO_CONFIG_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const [createSsoConfig] = useMutation(CREATE_SSO_CONFIG);
    const handleCreateSsoConfig = async (formData) => {
        try {
            await createSsoConfig({ variables: { input: formData } });
            await refetch();
            publishToast({ description: t('sso.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_SSO_PATH));
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sso.createError'), status: 'error' });

            if (e.message === 'Unknown provider') {
                form.setError('providerName', { message: t('sso.form.unknownProvider') });
            }
        }
    };

    return (
        <Modal
            visible={true}
            onClose={() => navigate(url(CUSTOMER_SSO_PATH))}
            title={t('sso.form.createTitle')}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_SSO_PATH)}>{t('sso.form.cancelButton')}</Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {t('sso.form.createButton')}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(handleCreateSsoConfig)} id="modal-form">
                <SsoConfigFields control={form.control} errors={formErrors} />
            </form>
        </Modal>
    );
};

const SsoConfigRow = ({ config, refetch }: { config: any; refetch: () => Promise<any> }) => {
    const { t } = useTranslation('customer');
    const { publishToast } = useToaster();
    const [isDeleting, setIsDeleting] = useState(false);

    const [deleteSsoConfig, { loading: deleteLoading }] = useMutation(DELETE_SSO_CONFIG);
    const handleDeleteConfig = async () => {
        try {
            await deleteSsoConfig({ variables: { id: config.id } });
            await refetch();
            publishToast({ description: t('sso.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sso.deleteError'), status: 'error' });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Table.Row>
            <Table.Cell>{config.providerName}</Table.Cell>
            <Table.Cell>{config.domain}</Table.Cell>
            <Table.Cell>{config.isRequired ? t('sso.table.yes') : t('sso.table.no')}</Table.Cell>
            <Table.Cell>{config.allowSelfRegistration ? t('sso.table.yes') : t('sso.table.no')}</Table.Cell>
            <Table.Cell sticky="right" className="min-w-14 w-14">
                <div className="flex gap-2">
                    <Tooltip content={t('sso.editTooltip')}>
                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_SSO_EDIT_PATH, { configId: config.id })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>
                    </Tooltip>

                    <Tooltip content={t('sso.deleteTooltip')}>
                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={() => setIsDeleting(true)}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </Tooltip>
                </div>

                {isDeleting && (
                    <ConfirmModal
                        onConfirm={handleDeleteConfig}
                        onCancel={() => setIsDeleting(false)}
                        title={t('sso.deleteConfirmTitle')}
                        labelConfirm={t('sso.deleteTooltip')}
                        confirmDisabled={deleteLoading}
                        danger
                    >
                        <span>{t('sso.deleteConfirmMessage', { name: config.providerName || config.domain })}</span>
                    </ConfirmModal>
                )}
            </Table.Cell>
        </Table.Row>
    );
};

const CustomerSsoPage = () => {
    const { t } = useTranslation('customer');

    const { data, loading, refetch } = useQuery(GET_SSO_CONFIGS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const configs = data?.ssoConfigs || [];

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('sso.title')}</Page.HeaderTitle>

                    <Page.HeaderButton asChild>
                        <Link to={url(CUSTOMER_SSO_ADD_PATH)}>
                            <AddIcon />
                            {t('sso.addButton')}
                        </Link>
                    </Page.HeaderButton>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell>{t('sso.table.providerName')}</Table.HeadCell>
                                <Table.HeadCell>{t('sso.table.domain')}</Table.HeadCell>
                                <Table.HeadCell>{t('sso.table.isRequired')}</Table.HeadCell>
                                <Table.HeadCell>{t('sso.table.allowSelfRegistration')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {configs.length > 0 ? (
                                configs.map((config) => (
                                    <SsoConfigRow key={config.id} config={config} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={5} className="text-center text-content-medium">
                                        {t('sso.noConfigs')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ refetch }} />
        </Layout>
    );
};

export default CustomerSsoPage;
